import { useMemo } from "react";
import jwtDecode from "jwt-decode";
import { AccessToken, User } from "./types";

const ASSUMED_USER_COOKIE = "pga_member_assumed_user";
const ASSUME_USER_RETURN_URL_COOKIE = "pga_member_assume_user_return_url";

const ONE_YEAR = 365 * 24 * 60 * 60;

const useImpersonation = (token: AccessToken | null) => {
  const canImpersonate = useMemo(() => {
    if (token) {
      try {
        const data = jwtDecode(token) as any;
        const scopes = (data?.scope || "").split(" ");
        return scopes.includes("impersonate:users");
      } catch (e) {}
    }
    return false;
  }, [token]);

  const assumedUserCookieValue = _getCookieValue(ASSUMED_USER_COOKIE);
  const impersonatedUser: User | null = assumedUserCookieValue ? JSON.parse(assumedUserCookieValue) : null;

  const assumeUser = async (user: User) => {
    _setCookieValue(ASSUMED_USER_COOKIE, JSON.stringify(user));
    _setCookieValue(ASSUME_USER_RETURN_URL_COOKIE, encodeURIComponent(window.location.href));
    window.location.assign("/member");
  };

  const clearAssumedUser = async () => {
    const returnUrl = _getCookieValue(ASSUME_USER_RETURN_URL_COOKIE);
    _clearCookie(ASSUMED_USER_COOKIE);
    _clearCookie(ASSUME_USER_RETURN_URL_COOKIE);

    if (returnUrl) {
      window.location.href = returnUrl;
    }
  };

  return {
    canImpersonate,
    impersonatedUser,
    impersonatedUserLoaded: true,
    assumeUser,
    clearAssumedUser,
  };
};

const _getCookieValue = (cookieName: string) => {
  const cookies = document.cookie.split(";");
  const cookiePairs = cookies.map((c) => c.split("="));
  const cookieKeyValue = cookiePairs.find((cp) => cp[0].trim() === cookieName);
  return cookieKeyValue ? decodeURIComponent(cookieKeyValue[1]) : null;
};

const _setCookieValue = (cookieName: string, value: string) => {
  document.cookie = `${cookieName}=${value}; path=/; max-age=${ONE_YEAR}; SameSite=Lax; ${
    process.env.NODE_ENV !== "development" ? "Secure;" : ""
  }`;
};

const _clearCookie = (cookieName: string) => {
  document.cookie = `${cookieName}=; path=/; max-age=0;`;
};

export default useImpersonation;
